import { supabase } from '../lib/supabase';

/**
 * Service for managing planner configurations and related data
 */
export const plannerService = {
  /**
   * Get all planners for the current user with preview images and download counts
   * @param {Object} filters - Filtering options (year, color scheme)
   * @param {string} sortBy - Sort order ('newest', 'oldest', 'name', 'downloads')
   * @param {number} page - Page number for pagination
   * @param {number} pageSize - Number of items per page
   * @returns {Promise<{planners: Array, totalCount: number, totalGenerated: number}>}
   */
  async getUserPlanners(filters = {}, sortBy = 'newest', page = 1, pageSize = 12) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Base query with join to preview images
      let query = supabase?.from('planner_configurations')?.select(`
          *,
          preview:planner_preview_images(thumbnail_data),
          downloads:downloads(download_count)
        `, { count: 'exact' })?.eq('user_id', user?.id);

      // Apply filters
      if (filters?.year) {
        query = query?.ilike('planner_name', `%${filters?.year}%`);
      }

      if (filters?.colorScheme && filters?.colorScheme !== 'all') {
        query = query?.eq('color_scheme', filters?.colorScheme);
      }

      // Apply sorting
      switch (sortBy) {
        case 'oldest':
          query = query?.order('created_at', { ascending: true });
          break;
        case 'name':
          query = query?.order('planner_name', { ascending: true });
          break;
        case 'newest':
        default:
          query = query?.order('created_at', { ascending: false });
          break;
      }

      // Apply pagination
      const start = (page - 1) * pageSize;
      const end = start + pageSize - 1;
      query = query?.range(start, end);

      const { data, error, count } = await query;
      if (error) throw error;

      // Get total count of planners ever created (including deleted)
      const { count: totalGenerated, error: countError } = await supabase?.from('planner_configurations')?.select('*', { count: 'exact', head: true })?.eq('user_id', user?.id);

      if (countError) throw countError;

      // Transform data to include download count and PDF path
      const planners = data?.map(planner => {
        const totalDownloads = planner?.downloads?.reduce((sum, d) => sum + (d?.download_count || 0), 0) || 0;
        
        return {
          id: planner?.id,
          name: planner?.planner_name,
          colorScheme: planner?.color_scheme,
          layoutType: planner?.layout_type,
          pageSize: planner?.page_size,
          isFavorite: planner?.is_favorite,
          configurationData: planner?.configuration_data,
          createdAt: planner?.created_at,
          updatedAt: planner?.updated_at,
          thumbnailData: planner?.preview?.[0]?.thumbnail_data,
          downloadCount: totalDownloads,
          generatedPdfPath: planner?.generated_pdf_path
        };
      });

      return {
        planners,
        totalCount: count || 0,
        totalGenerated: totalGenerated || 0,
        currentPage: page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    } catch (error) {
      console.error('Error fetching user planners:', error);
      throw error;
    }
  },

  /**
   * Delete a planner configuration (soft delete by removing from view)
   * Note: This tracks total planners created, even after deletion
   * @param {string} plannerId - ID of the planner to delete
   * @returns {Promise<void>}
   */
  async deletePlanner(plannerId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Delete related preview images first
      const { error: previewError } = await supabase?.from('planner_preview_images')?.delete()?.eq('config_id', plannerId)?.eq('user_id', user?.id);

      if (previewError) throw previewError;

      // Delete related downloads
      const { error: downloadsError } = await supabase?.from('downloads')?.delete()?.eq('planner_config_id', plannerId)?.eq('user_id', user?.id);

      if (downloadsError) throw downloadsError;

      // Delete the planner configuration
      const { error } = await supabase?.from('planner_configurations')?.delete()?.eq('id', plannerId)?.eq('user_id', user?.id);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting planner:', error);
      throw error;
    }
  },

  /**
   * Get unique years from planner names for filtering
   * @returns {Promise<Array<string>>}
   */
  async getAvailableYears() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('planner_configurations')?.select('planner_name')?.eq('user_id', user?.id);

      if (error) throw error;

      // Extract years from planner names (assuming format includes year)
      const years = new Set();
      data?.forEach(planner => {
        const yearMatch = planner?.planner_name?.match(/\b(20\d{2}|2025|2026)\b/);
        if (yearMatch) {
          years?.add(yearMatch?.[0]);
        }
      });

      return Array.from(years)?.sort((a, b) => b - a);
    } catch (error) {
      console.error('Error fetching available years:', error);
      return [];
    }
  },

  /**
   * Get signed URL for preview image from storage
   * @param {string} imagePath - Path to the image in storage
   * @returns {Promise<string>}
   */
  async getPreviewImageUrl(imagePath) {
    try {
      if (!imagePath) return null;

      const { data, error } = await supabase?.storage?.from('planner-previews')?.createSignedUrl(imagePath, 3600); // 1 hour expiry

      if (error) throw error;
      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting preview image URL:', error);
      return null;
    }
  },

  /**
   * Record a download for a planner
   * @param {string} plannerId - ID of the planner being downloaded
   * @returns {Promise<void>}
   */
  async recordDownload(plannerId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if download record exists
      const { data: existing, error: fetchError } = await supabase?.from('downloads')?.select('id, download_count')?.eq('user_id', user?.id)?.eq('planner_config_id', plannerId)?.maybeSingle();

      if (fetchError) throw fetchError;

      if (existing) {
        // Update existing download count
        const { error } = await supabase?.from('downloads')?.update({
            download_count: existing?.download_count + 1,
            download_date: new Date()?.toISOString()
          })?.eq('id', existing?.id);

        if (error) throw error;
      } else {
        // Create new download record
        const { error } = await supabase?.from('downloads')?.insert({
            user_id: user?.id,
            planner_config_id: plannerId,
            download_count: 1,
            file_format: 'PDF',
            file_size_mb: 2.5
          });

        if (error) throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error recording download:', error);
      throw error;
    }
  }
};